import {
    Box,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { WIDGET_URL } from '../../../../config';

export function buildSnippets({ venueSlug, showId, affiliateCode }) {
    const link = `${WIDGET_URL}/${venueSlug}-${showId}?affiliate=${affiliateCode}`;
    const embed = `<script type="module" src="${WIDGET_URL}/index.js"></script>
<div id="fireknife-widget-root" data-show="${showId}" data-affiliate="${affiliateCode}"></div>
<a href="javascript:window.showFireknifeReservation();">Book Now</a>`;
    const button = `<a href="javascript:window.showFireknifeReservation(${showId}, '${affiliateCode}');">Book Now</a>`;
    return { link, embed, button };
}

export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        /* clipboard may be unavailable */
    }
}

function SnippetField({ label, value, multiline, copyLabel }) {
    return (
        <TextField
            fullWidth
            size="small"
            label={label}
            value={value}
            multiline={multiline}
            minRows={multiline ? 3 : undefined}
            slotProps={{
                input: {
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={copyLabel}>
                                <IconButton size="small" onClick={() => copyToClipboard(value)}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                },
            }}
            onClick={(e) => {
                if (!multiline && e.target.select) e.target.select();
            }}
        />
    );
}

function CompactRow({ label, displayValue, copyValue, copyLabel }) {
    return (
        <TextField
            fullWidth
            size="small"
            value={displayValue}
            slotProps={{
                input: {
                    readOnly: true,
                    sx: { fontSize: 12, py: 0 },
                    startAdornment: (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                minWidth: 48,
                                mr: 1,
                                flexShrink: 0,
                                fontWeight: 600,
                            }}
                        >
                            {label}
                        </Typography>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={copyLabel}>
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(copyValue)}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                },
            }}
            onClick={(e) => e.target.select?.()}
        />
    );
}

function CompactSnippets({ link, embed, button, t }) {
    const copyLabel = t('commissionGroups.assignmentAffiliateCode.copy');
    // Display the embed on a single line (HTML is whitespace-insensitive),
    // but copy the original multi-line form for nicer paste.
    const embedDisplay = embed.replace(/\n+/g, ' ');
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <CompactRow
                label={t('commissionGroups.assignmentAffiliateCode.shortDirectLink')}
                displayValue={link}
                copyValue={link}
                copyLabel={copyLabel}
            />
            <CompactRow
                label={t('commissionGroups.assignmentAffiliateCode.shortEmbed')}
                displayValue={embedDisplay}
                copyValue={embed}
                copyLabel={copyLabel}
            />
            <CompactRow
                label={t('commissionGroups.assignmentAffiliateCode.shortButton')}
                displayValue={button}
                copyValue={button}
                copyLabel={copyLabel}
            />
        </Box>
    );
}

export default function AffiliateBookingSnippets({
    venueSlug,
    showId,
    affiliateCode,
    variant = 'expanded',
}) {
    const { t } = useTranslation();
    if (!venueSlug || !affiliateCode) return null;
    const { link, embed, button } = buildSnippets({ venueSlug, showId, affiliateCode });

    if (variant === 'compact') {
        return <CompactSnippets link={link} embed={embed} button={button} t={t} />;
    }

    const copyLabel = t('commissionGroups.assignmentAffiliateCode.copy');
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <SnippetField
                label={t('commissionGroups.assignmentAffiliateCode.directLinkLabel')}
                value={link}
                copyLabel={copyLabel}
            />
            <SnippetField
                label={t('commissionGroups.assignmentAffiliateCode.embedSnippetLabel')}
                value={embed}
                multiline
                copyLabel={copyLabel}
            />
            <SnippetField
                label={t('commissionGroups.assignmentAffiliateCode.buttonSnippetLabel')}
                value={button}
                copyLabel={copyLabel}
            />
        </Box>
    );
}
